/**
 * Copyright schukai GmbH and contributors 2023. All Rights Reserved.
 * Node module: @schukai/monster
 * This file is licensed under the AGPLv3 License.
 * License text available at https://www.gnu.org/licenses/agpl-3.0.en.html
 */

import { instanceSymbol } from "../constants.mjs";
import {
	hasObjectLink,
	getLinkedObjects,
	addToObjectLink,
} from "../dom/attributes.mjs";
import { getLocaleOfDocument } from "../dom/locale.mjs";
import { BaseWithOptions } from "../types/basewithoptions.mjs";
import { Locale } from "./locale.mjs";
import { Translations } from "./translations.mjs";

export { Provider, translationsLinkSymbol };

/**
 * @memberOf Monster.I18n
 * @type {symbol}
 * @license AGPLv3
 * @since 3.9.0
 * @private
 */
const translationsLinkSymbol = Symbol.for(
	"@schukai/monster/i18n/translations@@link",
);

/**
 * A provider makes a translation object available.
 *
 * @license AGPLv3
 * @since 1.13.0
 * @copyright schukai GmbH
 * @memberOf Monster.I18n
 * @see {@link https://datatracker.ietf.org/doc/html/rfc3066}
 */
class Provider extends BaseWithOptions {
	/**
	 * This method is called by the `instanceof` operator.
	 * @returns {symbol}
	 * @since 3.27.0
	 */
	static get [instanceSymbol]() {
		return Symbol.for("@schukai/monster/i18n/provider@@instance");
	}

	/**
	 * @param {Locale|string} locale
	 * @return {Promise}
	 */
	getTranslations(locale) {
		if (locale === undefined) {
			locale = getLocaleOfDocument();
		}

		return new Promise((resolve, reject) => {
			try {
				resolve(new Translations(locale));
			} catch (e) {
				reject(e);
			}
		});
	}

	/**
	 * @param {Locale|string} locale
	 * @param {HTMLElement} element
	 * @return {Provider}
	 */
	assignToElement(locale, element) {
		if (locale === undefined) {
			locale = getLocaleOfDocument();
		}

		if (!(locale instanceof Locale)) {
			throw new Error("Locale is not an instance of Locale");
		}

		if (!(element instanceof HTMLElement)) {
			element = document.querySelector("body");
		}

		if (!(element instanceof HTMLElement)) {
			throw new Error("Element is not an HTMLElement");
		}

		return this.getTranslations(locale).then((obj) => {
			let translations = null;
			if (hasObjectLink(element, translationsLinkSymbol)) {
				const objects = getLinkedObjects(element, translationsLinkSymbol);
				for (const o of objects) {
					if (o instanceof Translations) {
						translations = o;
						break;
					}
				}

				if (!(translations instanceof Translations)) {
					throw new Error("Object is not an instance of Translations");
				}

				translations.assignTranslations(obj);
			} else {
				addToObjectLink(element, translationsLinkSymbol, obj);
			}

			return obj;
		});
	}
}
